import React from 'react';
import { beautifyNumber } from '../../../Utils/FormatNumber';

const calcPercentage = (value, income) => {
  if (income <= 0) return '0.00%';
  const pct = (value / income) * 100;
  return pct.toFixed(2) + '%';
};

const wrapDoubleUnderline = (amount) => `
  <span class="accounting-amount-underline-double">
    <span class="accounting-amount-value">${amount}</span>
    <span class="accounting-underline-lines accounting-underline-lines-double">
      <span class="accounting-underline-line"></span>
      <span class="accounting-underline-line"></span>
    </span>
  </span>
`;

const wrapTripleUnderline = (amount) => `
  <span class="accounting-amount-underline-triple">
    <span class="accounting-amount-value">${amount}</span>
    <span class="accounting-underline-lines accounting-underline-lines-triple">
      <span class="accounting-underline-line"></span>
      <span class="accounting-underline-line"></span>
      <span class="accounting-underline-line"></span>
    </span>
  </span>
`;

const identityWrap = (amount) => `${amount}`;

const sumFcField = (txns, field) =>
  (txns || []).reduce((acc, tr) => acc + (Number(tr?.[field]) || 0), 0);

// Render foreign-currency total cells for a period.
//  - fc: { showFc, showCards }
//  - values: { debit, credit, balance }
//  - detailed (showCards) -> חובה/זכות/יתרה מט"ח, otherwise -> יתרה מט"ח only.
const renderFcTotalCells = (cls, wrap, fc, values) => {
  if (!fc || !fc.showFc) return '';
  const w = wrap || identityWrap;
  if (fc.showCards) {
    return `<td class="${cls} td-fc">${w(beautifyNumber(values.debit || 0))}</td>` +
      `<td class="${cls} td-fc">${w(beautifyNumber(values.credit || 0))}</td>` +
      `<td class="${cls} td-fc">${w(beautifyNumber(values.balance || 0))}</td>`;
  }
  return `<td class="${cls} td-fc">${w(beautifyNumber(values.balance || 0))}</td>`;
};

const fcValuesFromTxns = (txns) => ({
  debit: sumFcField(txns, 'foreignCurrencyDebit'),
  credit: sumFcField(txns, 'foreignCurrencyCredit'),
  balance: sumFcField(txns, 'foreignCurrencyBalance'),
});

const TableTotals = {
  // Generate totals for a specific palCode
  generatePalCodeTotals: (transactions, palCode, showPeriodComparison, comparisonTransactions, comparisonTotalIncome, totalIncome, showPercentageColumn, t, fc = null) => {
    const totalBalance = transactions.reduce((acc, t) => acc + t.balance, 0);

    let comparisonTotalBalance = 0;
    let comparisonPercentage = '0.00%';
    let comparisonPalTransactions = [];

    if (showPeriodComparison && comparisonTransactions) {
      comparisonPalTransactions = comparisonTransactions.filter(ctr => ctr.palCode === palCode);
      comparisonTotalBalance = comparisonPalTransactions.reduce((acc, t) => acc + t.balance, 0);
      comparisonPercentage = calcPercentage(comparisonTotalBalance, comparisonTotalIncome);
    }

    const firstPeriodPct = calcPercentage(totalBalance, totalIncome);

    return `
      <tr class="tr-final-row-palcode">
        <td class="total-palcode">סה''כ: ${palCode === 'NonPalCode' ? t('NonPalCode') : palCode} </td>
        ${fc?.fcOnly ? '' : `<td class="td-last">${wrapDoubleUnderline(beautifyNumber(totalBalance))}</td>`}
        ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-percentage">${wrapDoubleUnderline(firstPeriodPct)}</td>` : ''}
        ${renderFcTotalCells('td-last', wrapDoubleUnderline, fc, fcValuesFromTxns(transactions))}
        ${showPeriodComparison ? `
            ${fc?.fcOnly ? '' : `<td class="td-last">${wrapDoubleUnderline(beautifyNumber(comparisonTotalBalance))}</td>`}
            ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-percentage">${wrapDoubleUnderline(comparisonPercentage)}</td>` : ''}
            ${renderFcTotalCells('td-last', wrapDoubleUnderline, fc, fcValuesFromTxns(comparisonPalTransactions))}
        ` : ''}
      </tr>
    `;
  },

  // Generate totals for a palCodeCategory
  generatePalCodeCategoryTotals: (
    transactions, 
    palCodeCategory, 
    accountingGroupName, 
    showPeriodComparison,
    comparisonTransactions, 
    comparisonTotalIncome,
    totalIncome,
    showPercentageColumn,
    profitAndLossTotalBalance1,
    comparisonProfitAndLossTotalBalance,
    firstPeriodData,
    t,
    isLastCategoryInGroup,
    fc = null
  ) => {
    // When a single currency is selected, group totals derived from raw period data must
    // be restricted to rows that carry foreign-currency activity.
    const fcRowFilter = (rows) =>
      (fc && typeof fc.selectedCurrency === 'number')
        ? rows.filter(r =>
          (Number(r?.foreignCurrencyDebit) || 0) !== 0 ||
          (Number(r?.foreignCurrencyCredit) || 0) !== 0 ||
          (Number(r?.foreignCurrencyBalance) || 0) !== 0)
        : rows;

    const totalBalance = transactions.reduce((acc, t) => acc + t.balance, 0);

    let comparisonTotalBalance = 0;
    let comparisonPercentage = '0.00%';
    let comparisonCategoryTransactions = [];

    if (showPeriodComparison && comparisonTransactions) {
      comparisonCategoryTransactions = comparisonTransactions.filter(ctr => ctr.palCodeCategory === palCodeCategory);
      comparisonTotalBalance = comparisonCategoryTransactions.reduce((acc, t) => acc + t.balance, 0);
      comparisonPercentage = calcPercentage(comparisonTotalBalance, comparisonTotalIncome);
    }

    const categoryPct = calcPercentage(totalBalance, totalIncome);

    let html = `
      <tr class="tr-final-row-palcode-category ag-classification-total-row">
        <td class="total-palcode">
          סה''כ: ${palCodeCategory === 'NonPalCodeCategory'
        ? t('NonPalCodeCategory')
        : palCodeCategory}
        </td>
        ${fc?.fcOnly ? '' : `<td class="td-last">${wrapDoubleUnderline(beautifyNumber(totalBalance))}</td>`}
        ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-percentage">${wrapDoubleUnderline(categoryPct)}</td>` : ''}
        ${renderFcTotalCells('td-last', wrapDoubleUnderline, fc, fcValuesFromTxns(transactions))}
        ${showPeriodComparison ? `
            ${fc?.fcOnly ? '' : `<td class="td-last">${wrapDoubleUnderline(beautifyNumber(comparisonTotalBalance))}</td>`}
            ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-percentage">${wrapDoubleUnderline(comparisonPercentage)}</td>` : ''}
            ${renderFcTotalCells('td-last', wrapDoubleUnderline, fc, fcValuesFromTxns(comparisonCategoryTransactions))}
        ` : ''}
      </tr>
    `;

    if (isLastCategoryInGroup) {
      const groupTransactions = fcRowFilter((firstPeriodData?.transaction || []).filter(
        t => t.palCodeAccountGroup === accountingGroupName
      ));
      const groupTotalBalance = groupTransactions.reduce((acc, t) => acc + t.balance, 0);
      const groupPct = calcPercentage(groupTotalBalance, totalIncome);

      let comparisonGroupTotalBalance = 0;
      let comparisonGroupPct = '0.00%';
      let comparisonGroupTransactions = [];

      if (showPeriodComparison && comparisonTransactions) {
        comparisonGroupTransactions = fcRowFilter(comparisonTransactions.filter(
          t => t.palCodeAccountGroup === accountingGroupName
        ));
        comparisonGroupTotalBalance = comparisonGroupTransactions.reduce((acc, t) => acc + t.balance, 0);
        comparisonGroupPct = calcPercentage(comparisonGroupTotalBalance, comparisonTotalIncome);
      }

      const plPct = calcPercentage(profitAndLossTotalBalance1, totalIncome);
      const compPlPct = calcPercentage(comparisonProfitAndLossTotalBalance, comparisonTotalIncome);

      const plFc = fc?.plFc || { debit: 0, credit: 0, balance: 0 };
      const compPlFc = fc?.compPlFc || { debit: 0, credit: 0, balance: 0 };
      const emptyFcCells = !fc || !fc.showFc ? '' :
        (fc.showCards ? '<td class="td-last-sum td-fc"></td><td class="td-last-sum td-fc"></td><td class="td-last-sum td-fc"></td>' : '<td class="td-last-sum td-fc"></td>');

      html += `
        <tr class="tr-final-row-palcode-category pal-code-account-group-total-row">
          <td class="total-palcode">סה''כ ${accountingGroupName}:</td>
          ${fc?.fcOnly ? '' : `<td class="td-last-sum">${wrapTripleUnderline(beautifyNumber(groupTotalBalance))}</td>`}
          ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum td-percentage">${wrapTripleUnderline(groupPct)}</td>` : ''}
          ${renderFcTotalCells('td-last-sum', wrapTripleUnderline, fc, fcValuesFromTxns(groupTransactions))}
          ${showPeriodComparison ? `
              ${fc?.fcOnly ? '' : `<td class="td-last-sum">${wrapTripleUnderline(beautifyNumber(comparisonGroupTotalBalance))}</td>`}
              ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum td-percentage">${wrapTripleUnderline(comparisonGroupPct)}</td>` : ''}
              ${renderFcTotalCells('td-last-sum', wrapTripleUnderline, fc, fcValuesFromTxns(comparisonGroupTransactions))}
          ` : ''}
        </tr>
        <tr class="tr-final-row-palcode-category">
          ${accountingGroupName !== "הכנסות" ? `<td class="total-palcode">סה''כ רווח והפסד לאחר ${accountingGroupName}:</td>` : `<td class="total-palcode"></td>`}
          ${fc?.fcOnly
            ? ''
            : (accountingGroupName !== "הכנסות" ? `<td class="td-last-sum">${wrapTripleUnderline(beautifyNumber(profitAndLossTotalBalance1))}</td>` : `<td class="td-last-sum"></td>`)}
          ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum td-percentage">${wrapTripleUnderline(plPct)}</td>` : ''}
          ${accountingGroupName !== "הכנסות" ? renderFcTotalCells('td-last-sum', wrapTripleUnderline, fc, plFc) : emptyFcCells}
          ${showPeriodComparison ? `
              ${fc?.fcOnly ? '' : `<td class="td-last-sum">${wrapTripleUnderline(beautifyNumber(comparisonProfitAndLossTotalBalance))}</td>`}
              ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum td-percentage">${wrapTripleUnderline(compPlPct)}</td>` : ''}
              ${accountingGroupName !== "הכנסות" ? renderFcTotalCells('td-last-sum', wrapTripleUnderline, fc, compPlFc) : emptyFcCells}
          ` : ''}
        </tr>
      `;
    }

    return html;
  },

  // Generate final summary row
  generateFinalSummaryRow: (
    profitAndLossTotalBalance1,
    comparisonProfitAndLossTotalBalance,
    showPercentageColumn,
    showPeriodComparison,
    totalIncome,
    comparisonTotalIncome,
    fc = null
  ) => {
    const finalPct = calcPercentage(profitAndLossTotalBalance1, totalIncome);
    const compFinalPct = calcPercentage(comparisonProfitAndLossTotalBalance, comparisonTotalIncome);
    const plFc = fc?.plFc || { debit: 0, credit: 0, balance: 0 };
    const compPlFc = fc?.compPlFc || { debit: 0, credit: 0, balance: 0 };

    return `
      <tr class="tr-final-row-palcode-Report">
        <td class="total-palcode-final">סה''כ רווח והפסד בדוח: </td>
        ${fc?.fcOnly ? '' : `<td class="td-last-sum-1">${beautifyNumber(profitAndLossTotalBalance1)}</td>`}
        ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum-1">${finalPct}</td>` : ''}
        ${renderFcTotalCells('td-last-sum-1', identityWrap, fc, plFc)}
        ${showPeriodComparison ? `
            ${fc?.fcOnly ? '' : `<td class="td-last-sum-1">${beautifyNumber(comparisonProfitAndLossTotalBalance)}</td>`}
            ${showPercentageColumn && !fc?.fcOnly ? `<td class="td-last-sum-1">${compFinalPct}</td>` : ''}
            ${renderFcTotalCells('td-last-sum-1', identityWrap, fc, compPlFc)}
        ` : ''}
      </tr>
    `;
  }
};

export default TableTotals;
